import { useId, useState } from "react";
import { Link } from "react-router";

import { EmptyState } from "../lib/EmptyState.tsx";
import { WorkThumb } from "../lib/WorkThumb.tsx";
import { SettlementRow } from "./SettlementRow.tsx";
import { SETTLEMENT_COPY } from "./settlements.constants.ts";
import type { LockedRow, SettlementRow as Row } from "./settlements.model.ts";
import { useSettlementsController } from "./useSettlementsController.ts";
import "./gallery.css";
import "./settlements.css";

/**
 * What this gallery's post-sale covenants have produced.
 *
 * The screen the portal's `notYet` copy described for a year — "the terms are
 * recorded, what is owed under them is not computed yet" — and the row it said
 * was missing is `ondo.resale_settlements`.
 *
 * FOUR GROUPS, and the last two are the ones a director will not expect. A
 * resale at a loss owes nothing and STILL WRITES A ROW, because the row is the
 * record that the covenant was tested; and a live covenant that locks is not a
 * settlement at all, but it is the reason a settlement somebody expected is not
 * here, so it is on the screen where they will look for it.
 *
 * IT READS THE TABLE, NEVER A FEED. `record()` and `notify()` swallow their
 * failures, so a feed line can be silently missing while the row exists — and
 * a screen answering "what is owed" from a feed would be answering "what
 * happened" instead.
 *
 * It carries no title of its own: the portal header above it does.
 *
 * `canSettle` IS THE ADMIN RUNG, and it hides controls rather than powers them.
 * `ondo.settle_resale()`, `ondo.answer_settlement()` and
 * `ondo.unlock_covenant()` all require admin on the covenant-holding firm, so
 * a registrar pressing Collect got a raw 42501 rendered as an error string —
 * which reads as a fault and is not one. The database still decides; this only
 * declines to offer a press that is certain to be refused.
 */
export function Settlements({
  workBase,
  canSettle,
}: {
  workBase: string;
  canSettle: boolean;
}) {
  const { view, working, rating, openRate, closeRate, stateRate, settle, lift } =
    useSettlementsController(workBase);

  if (view.kind === "loading") {
    return (
      <p className="gallery__note" role="status">
        {view.message}
      </p>
    );
  }

  if (view.kind === "error") {
    return (
      <section className="gallery__note gallery__note--error" role="alert">
        <h2 className="gallery__note-title">{view.title}</h2>
        <p>{view.message}</p>
      </section>
    );
  }

  if (view.kind === "empty") {
    return (
      <EmptyState className="gallery__empty" lead={view.message} detail={view.detail} />
    );
  }

  return (
    <>
      <p className="gallery__summary">{view.summary}</p>

      {/* The banner corrects the two things "automatic" invites a reader to
          assume: that ONDO takes the money, and that it catches every resale. */}
      <section className="gallery__lead">
        <p>
          <strong>{SETTLEMENT_COPY.lead}</strong> {SETTLEMENT_COPY.leadDetail}
        </p>
      </section>

      {view.due.length > 0 && (
        <section>
          <div className="gallery__group">
            <h2 className="gallery__group-title">
              {SETTLEMENT_COPY.groups.due} ({view.due.length})
            </h2>
            {view.dueSummary && (
              <span className="gallery__group-note">{view.dueSummary}</span>
            )}
          </div>

          <ul className="gallery__settlements">
            {view.due.map((row) => (
              <SettlementRow
                key={row.settlementId}
                row={row}
                action={
                  canSettle ? (
                    <Control
                      row={row}
                      busy={working === row.settlementId}
                      rating={rating === row.settlementId}
                      onOpenRate={() => openRate(row.settlementId)}
                      onCloseRate={closeRate}
                      onRate={(value) => stateRate(row.settlementId, value)}
                      onSettle={(leg) => settle(row.settlementId, leg)}
                    />
                  ) : (
                    // Says WHY there is no button, rather than showing none.
                    // What is owed is still the reader's business; recording
                    // that it moved is not theirs to do.
                    <p className="gallery__note gallery__note--quiet">
                      {SETTLEMENT_COPY.needsAdmin}
                    </p>
                  )
                }
              />
            ))}
          </ul>
        </section>
      )}

      {view.closed.length > 0 && (
        <section>
          <div className="gallery__group">
            <h2 className="gallery__group-title">
              {SETTLEMENT_COPY.groups.closed} ({view.closed.length})
            </h2>
          </div>
          <ul className="gallery__settlements">
            {view.closed.map((row) => (
              <SettlementRow key={row.settlementId} row={row} action={null} />
            ))}
          </ul>
        </section>
      )}

      {view.nothingOwed.length > 0 && (
        <section>
          <div className="gallery__group">
            <h2 className="gallery__group-title">
              {SETTLEMENT_COPY.groups.nothingOwed} ({view.nothingOwed.length})
            </h2>
          </div>
          <ul className="gallery__settlements">
            {view.nothingOwed.map((row) => (
              <SettlementRow key={row.settlementId} row={row} action={null} />
            ))}
          </ul>
        </section>
      )}

      {view.locked.length > 0 && (
        <section>
          <div className="gallery__group">
            <h2 className="gallery__group-title">
              {SETTLEMENT_COPY.groups.locked} ({view.locked.length})
            </h2>
            <span className="gallery__group-note">
              {SETTLEMENT_COPY.groups.lockedNote}
            </span>
          </div>
          <ul className="gallery__settlements">
            {view.locked.map((row) => (
              <LockedCard
                key={row.transferId}
                row={row}
                busy={working === row.transferId}
                canLift={canSettle}
                onLift={() => lift(row.transferId)}
              />
            ))}
          </ul>
        </section>
      )}
    </>
  );
}

/**
 * The row's control, and the rate dialogue that one of them opens.
 *
 * A dialogue rather than a bare button on the cross-currency row, because that
 * press does two irreversible things at once: it states a rate that can never
 * be restated, and it records a collection. The other legs need no dialogue —
 * they assert a fact the director already knows.
 */
function Control({
  row,
  busy,
  rating,
  onOpenRate,
  onCloseRate,
  onRate,
  onSettle,
}: {
  row: Row;
  busy: boolean;
  rating: boolean;
  onOpenRate: () => void;
  onCloseRate: () => void;
  onRate: (rate: number) => void;
  onSettle: (leg: "collect" | "remit") => void;
}) {
  const action = row.action;
  if (action === null) return null;

  switch (action.kind) {
    case "rate":
      return rating ? (
        <RateForm row={row} busy={busy} onCancel={onCloseRate} onRate={onRate} />
      ) : (
        <button type="button" className="gallery__settle" disabled={busy} onClick={onOpenRate}>
          {action.label} <span aria-hidden="true">→</span>
        </button>
      );
    case "settle":
      return (
        <button
          type="button"
          className="gallery__settle"
          disabled={busy}
          onClick={() => onSettle(action.leg)}
        >
          {action.label} <span aria-hidden="true">→</span>
        </button>
      );
  }
}

function RateForm({
  row,
  busy,
  onCancel,
  onRate,
}: {
  row: Row;
  busy: boolean;
  onCancel: () => void;
  onRate: (rate: number) => void;
}) {
  const fieldId = useId();
  const [value, setValue] = useState("");
  const parsed = Number(value);
  const valid = value.trim() !== "" && Number.isFinite(parsed) && parsed > 0;

  return (
    <form
      className="gallery__rate"
      onSubmit={(event) => {
        event.preventDefault();
        if (valid) onRate(parsed);
      }}
    >
      <label className="gallery__rate-label" htmlFor={fieldId}>
        {row.rateLabel}
      </label>
      <input
        id={fieldId}
        className="gallery__rate-input"
        type="number"
        step="any"
        min="0"
        inputMode="decimal"
        value={value}
        onChange={(event) => setValue(event.target.value)}
      />
      <p className="gallery__rate-hint">{SETTLEMENT_COPY.rate.hint}</p>
      <div className="gallery__rate-actions">
        <button type="submit" className="gallery__settle" disabled={busy || !valid}>
          {SETTLEMENT_COPY.rate.confirm}
        </button>
        <button type="button" className="gallery__rate-cancel" onClick={onCancel}>
          {SETTLEMENT_COPY.rate.cancel}
        </button>
      </div>
    </form>
  );
}

/**
 * A live covenant that stops the work being sold on.
 *
 * Not a settlement, and the card says less because there is less to say: no
 * arithmetic has happened and none can until somebody lifts the lock. The
 * control is `unlockCovenant()`, which has had no caller anywhere in this app
 * since it was written.
 */
function LockedCard({
  row,
  busy,
  canLift,
  onLift,
}: {
  row: LockedRow;
  busy: boolean;
  /** Admin on the covenant-holding firm. `ondo.unlock_covenant()` requires it. */
  canLift: boolean;
  onLift: () => void;
}) {
  return (
    <li className="gallery__settlement gallery__settlement--locked">
      <Link className="gallery__settlement-open" to={row.href}>
        <WorkThumb imageUrl={row.imageUrl} title={row.title} />
        <div className="gallery__settlement-body">
          <p className="gallery__settlement-title">
            {row.title}
            <span className="gallery__settlement-state gallery__settlement-state--void">
              {SETTLEMENT_COPY.locked}
            </span>
          </p>
          <p className="gallery__settlement-meta">{row.identity}</p>
          <p className="gallery__settlement-meta">{row.sale}</p>
        </div>
        {row.wouldOwe && (
          <div className="gallery__settlement-due">
            <span className="gallery__settlement-duelabel">
              {SETTLEMENT_COPY.dueLabel}
            </span>
            <span className="gallery__settlement-dueval gallery__settlement-dueval--pending">
              {row.wouldOwe}
            </span>
          </div>
        )}
      </Link>

      <div className="gallery__settlement-foot">
        <p className="gallery__settlement-note">{SETTLEMENT_COPY.lockedDetail}</p>
        {canLift ? (
          <button type="button" className="gallery__lift" disabled={busy} onClick={onLift}>
            {SETTLEMENT_COPY.actions.unlock}
          </button>
        ) : (
          <p className="gallery__note gallery__note--quiet">{SETTLEMENT_COPY.needsAdmin}</p>
        )}
      </div>
    </li>
  );
}
